import React from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { Box, Paper, Skeleton, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';

import { RouterOutput, trpc } from '../api/trpc';
import { Group } from '../organisms/group';
import { Row } from '../atoms/row';
import { useSelector } from '../store/utils';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  CardPieChart,
  defaultKwh,
  useCardShare,
} from '../organisms/reports/report-card';
import { useFormatNumber } from '../utils/format-number';
import { colors } from '../theme/palette';

const Reports = () => {
  const premium = useSelector(({ variables: { PREMIUM } }) => PREMIUM);

  const { data, isLoading, isError } = trpc.reports.list.useQuery();

  if (!premium) return <Navigate to="/" replace />;

  return (
    <>
      <Group layout="list">
        {isError && (
          <Typography color="error">
            <FormattedMessage
              id="error"
              defaultMessage="Chyba při načítání reportu."
            />
          </Typography>
        )}

        {isLoading && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
            }}
          >
            <Skeleton variant="rounded" height={150} />
            <Skeleton variant="rounded" height={150} />
            <Skeleton variant="rounded" height={150} />
            <Skeleton variant="rounded" height={150} />
            <Skeleton variant="rounded" height={150} />
            <Skeleton variant="rounded" height={150} />
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          {data &&
            data.map((report) => {
              return <ReportCard key={report.month} report={report} />;
            })}
        </Box>
      </Group>
    </>
  );
};

const pieChartDataPlaceholder = [
  { value: 50, label: '', color: '#989898' },
  { value: 50, label: '', color: '#606060' },
];

type ReportCardProps = {
  report: RouterOutput['reports']['list'][number];
};

const ReportCard = ({ report }: ReportCardProps) => {
  const formatNumber = useFormatNumber();
  const navigate = useNavigate();

  const { month } = report;

  const title = (
    <FormattedDate
      value={new Date(month)}
      day={undefined}
      month="long"
      year="numeric"
    />
  );

  const pieChart =
    report.status === 'generated'
      ? [
          {
            label: (
              <FormattedMessage
                id="reports.pv_consumption"
                defaultMessage="Spotřeba z FVE"
                key={0}
              />
            ),
            labelValue: formatNumber(
              report.payload.pvProduction - report.payload.emOverflow,
              defaultKwh
            ),
            value: report.payload.pvProduction - report.payload.emOverflow,
            color: colors.primary,
          },
          {
            label: (
              <FormattedMessage
                id="reports.grid_consumption"
                defaultMessage="Odběr ze sítě"
                key={0}
              />
            ),
            labelValue: formatNumber(report.payload.emConsumed, defaultKwh),
            value: report.payload.emConsumed,
            color: colors.darkBlue,
          },
        ]
      : undefined;

  const { share } = useCardShare({ title, pieChart });

  return (
    <Paper
      key={month}
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
      onClick={() => navigate(`/reporty/${month}`)}
    >
      <Row sx={{ gap: 1 }}>
        <Typography variant="h3" sx={{ textTransform: 'capitalize' }}>
          {title}
        </Typography>
        <Box
          sx={{
            color: '#989898',
            '& svg': { fontSize: '1.5rem' },
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ChevronRight />
        </Box>

        <Box onClick={(e) => e.stopPropagation()} sx={{ ml: 'auto' }}>
          {share}
        </Box>
      </Row>

      <CardPieChart
        data={pieChart || pieChartDataPlaceholder}
        disableAnimation
        legendX={
          pieChart ? undefined : (
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              <FormattedMessage
                id="report.not_generated"
                defaultMessage="Vytvořit report kliknutím"
              />
            </Typography>
          )
        }
      />
    </Paper>
  );
};

export default Reports;
